import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Link, useParams } from 'react-router-dom';
import AppSidebar from './AppSidebar';
import { Navbar } from './common/Catalyst/navbar';
import { SidebarLayout } from './common/Catalyst/sidebar-layout';
import LoadingSpinner from './LoadingSpinner';
import { amenityService, reservationOperationsService } from './clients/ApiClients';
import { ReservationRevenueMonth, ReservationRevenueReport } from './clients/rust-api-client';
import { formatMoney, isNegativeMoney } from './utils/billingHelpers';
import { formatDayLabel, reservationErrorMessage, todayIn, tzLabel } from './reservation/reservationMeta';
import { RANGE_LABELS, RangePreset, presetRange } from './reservation/utilizationMeta';
import {
  REVENUE_FIELDS,
  REVENUE_LABELS,
  RevenueField,
  depositsStillHeld,
  formatMonth,
} from './reservation/phaseThreeMeta';
import { inputClass, labelClass } from './reservation/formStyles';
import { ErrorAlert } from './common/Custom/ErrorAlert';
import { FieldPicker } from './common/Custom/FieldPicker';

/**
 * What the association's amenities earned (#642, phase 3), by amenity and
 * month: fees charged, reversed and late-cancellation fees, net; and
 * deposits charged, returned, owed back and kept. Read-only; the board
 * sees it too.
 *
 * Deposits are a liability, not revenue — only the part a settlement kept
 * becomes income — so they sit in their own column group. Amounts are
 * the API's decimal strings, shown as they come; nothing is added up in
 * floating point here.
 */
const FEE_FIELDS: RevenueField[] = ['fees_charged', 'fees_reversed', 'late_cancel_fees', 'net_fee_revenue'];
const DEPOSIT_FIELDS: RevenueField[] = ['deposits_charged', 'deposits_returned', 'deposits_refund_due', 'deposit_deductions'];

const ReservationRevenuePage: React.FC = () => {
  const { hoaId } = useParams<{ hoaId: string }>();
  const [zone, setZone] = useState<string | null>(null);
  const [preset, setPreset] = useState<RangePreset | 'custom'>('last90');
  const [range, setRange] = useState(() => presetRange('last90', todayIn(null)));
  const [amenityId, setAmenityId] = useState('');
  const [report, setReport] = useState<ReservationRevenueReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!hoaId) return;
    amenityService
      .listAmenities({ hoaId })
      .then((r) => setZone(r.data.amenities[0]?.timezone ?? null))
      .catch(() => undefined);
  }, [hoaId]);

  useEffect(() => {
    if (!hoaId || !range.from || !range.to || range.from > range.to) return;
    let live = true;
    setLoading(true);
    setError(null);
    reservationOperationsService
      .getReservationRevenue({ hoaId, from: range.from, to: range.to, amenityId: amenityId || undefined })
      .then((r) => live && setReport(r.data))
      .catch((err) => live && setError(reservationErrorMessage(err, "Couldn't load the report.")))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [hoaId, range, amenityId]);

  const choosePreset = (p: RangePreset | 'custom') => {
    setPreset(p);
    if (p !== 'custom') setRange(presetRange(p, todayIn(zone)));
  };

  const toggle = (id: string) =>
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const thisYear = new Date().getFullYear();
  const amenities = report?.amenities ?? [];

  const cells = (m: ReservationRevenueMonth, strong = false) =>
    REVENUE_FIELDS.map((f) => (
      <td
        key={f}
        className={clsx(
          'whitespace-nowrap px-3 py-2 text-right',
          f === 'net_fee_revenue' && 'font-medium',
          FEE_FIELDS.includes(f) ? '' : 'text-fg-secondary',
          f === DEPOSIT_FIELDS[0] && 'border-l border-line/60',
          strong && 'font-medium',
          isNegativeMoney(m[f]) && 'text-danger'
        )}
      >
        {formatMoney(m[f])}
      </td>
    ));

  return (
    <SidebarLayout sidebar={<AppSidebar currentPage="reservations" />} navbar={<Navbar>{/* Your navbar content */}</Navbar>}>
      <div className="-mx-2 mb-6 flex items-center gap-2 border-b border-line/60 px-2 pb-3 text-[13px]">
        <Link to={`/hoa/${hoaId}/reservations`} className="shrink-0 text-fg-muted hover:text-fg">
          Reservations
        </Link>
        <span className="text-fg-faint" aria-hidden>
          /
        </span>
        <span className="min-w-0 truncate font-medium text-fg">Revenue</span>
        <Link to={`/hoa/${hoaId}/reservations/utilization`} className="ml-auto text-[12px] text-fg-muted hover:text-fg">
          Utilization
        </Link>
      </div>

      <div className="space-y-6">
        <div className="flex flex-wrap items-end gap-3">
          <h1 className="mr-auto text-2xl/8 font-semibold tracking-tight text-fg">Amenity revenue</h1>
          <div className={labelClass}>
            Range
            <FieldPicker
              size="sm"
              className="w-40"
              label="Range"
              value={preset}
              onChange={(v) => choosePreset(v as RangePreset | 'custom')}
              options={[
                ...(Object.keys(RANGE_LABELS) as RangePreset[]).map((p) => ({ value: p, label: RANGE_LABELS[p] })),
                { value: 'custom', label: 'Custom…' },
              ]}
            />
          </div>
          {preset === 'custom' && (
            <>
              <label className={labelClass}>
                From
                <input type="date" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className={inputClass} />
              </label>
              <label className={labelClass}>
                To
                <input type="date" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className={inputClass} />
              </label>
            </>
          )}
        </div>
        <p className="text-[12px] text-fg-muted">
          {formatDayLabel(range.from, thisYear)} – {formatDayLabel(range.to, thisYear)} · {tzLabel(zone)}
          {amenityId && (
            <button type="button" className="ml-2 text-accent-fg hover:underline" onClick={() => setAmenityId('')}>
              Show every amenity
            </button>
          )}
        </p>

        <ErrorAlert>{error}</ErrorAlert>
        {loading && !report ? (
          <LoadingSpinner />
        ) : amenities.length === 0 ? (
          <p className="text-[13px] text-fg-muted">No amenity charges in this range.</p>
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-line">
              <table className="w-full text-[13px] tabular-nums">
                <thead className="border-b border-line text-left text-[12px] text-fg-muted">
                  <tr>
                    <th className="px-3 pt-2 font-medium" />
                    <th colSpan={FEE_FIELDS.length} className="px-3 pt-2 text-right font-medium text-fg-secondary">
                      Fees
                    </th>
                    <th colSpan={DEPOSIT_FIELDS.length} className="border-l border-line/60 px-3 pt-2 text-right font-medium text-fg-secondary">
                      Deposits (held, not income)
                    </th>
                  </tr>
                  <tr>
                    <th className="px-3 py-2 font-medium">Amenity</th>
                    {REVENUE_FIELDS.map((f) => (
                      <th
                        key={f}
                        className={clsx('whitespace-nowrap px-3 py-2 text-right font-medium', f === DEPOSIT_FIELDS[0] && 'border-l border-line/60')}
                      >
                        {REVENUE_LABELS[f]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-line/50">
                  {amenities.map((a) => {
                    const open = expanded.has(a.amenity_id);
                    return (
                      <React.Fragment key={a.amenity_id}>
                        <tr>
                          <td className="px-3 py-2">
                            <button
                              type="button"
                              onClick={() => toggle(a.amenity_id)}
                              aria-expanded={open}
                              className="mr-1 text-fg-muted hover:text-fg"
                              aria-label={`${open ? 'Hide' : 'Show'} months for ${a.name}`}
                            >
                              {open ? '▾' : '▸'}
                            </button>
                            <button type="button" className="text-fg hover:underline" onClick={() => setAmenityId(a.amenity_id)}>
                              {a.name}
                            </button>
                          </td>
                          {cells(a.totals)}
                        </tr>
                        {open &&
                          a.months.map((m) => (
                            <tr key={m.month} className="bg-fg/[0.02] text-[12px]">
                              <td className="px-3 py-1.5 pl-8 text-fg-muted">{formatMonth(m.month)}</td>
                              {cells(m)}
                            </tr>
                          ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                {report && amenities.length > 1 && (
                  <tfoot className="border-t border-line">
                    <tr>
                      <td className="px-3 py-2 font-medium">All amenities</td>
                      {cells(report.totals, true)}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            {report && (
              <p className="text-[12px] text-fg-muted">
                Net fees are charged less reversed, plus late-cancellation fees. Of the deposits charged in this range,{' '}
                {formatMoney(depositsStillHeld(report.totals))} is still held. “Kept” is what settlements kept as amenity
                income; “Owed back” is paid deposits waiting for a refund. Card payment is not offered — every amount
                here went through the homes’ accounts.
              </p>
            )}
          </>
        )}
      </div>
    </SidebarLayout>
  );
};

export default ReservationRevenuePage;
